import TypographyParagraph from '../../typography/TypographyParagraph'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { Suspense, useRef } from 'react'
import {
	asyncTruncateStringInMiddle,
	mapStatus,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import { Await } from '@remix-run/react'

export default function OffchainLendTemplate({
	offchainLend,
}: {
	offchainLend: any
}) {
	const tooltipCopyStartDate = useRef<TooltipRefProps>(null)
	const tooltipCopyEndDate = useRef<TooltipRefProps>(null)
	const tooltipCopyLenderAddress = useRef<TooltipRefProps>(null)
	const tooltipCopyLendeeAddress = useRef<TooltipRefProps>(null)
	const tooltipCopyAmount = useRef<TooltipRefProps>(null)
	const tooltipCopyCurrency = useRef<TooltipRefProps>(null)
	const tooltipCopyComment = useRef<TooltipRefProps>(null)
	const tooltipCopySignature = useRef<TooltipRefProps>(null)

	return (
		<>
			<div className="flex flex-col sm:flex-row">
				<div
					id="startDate"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(offchainLend.properties.startDate)
						tooltipCopyStartDate.current?.open({
							anchorSelect: '#startDate',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyStartDate.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyStartDate} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Start date:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainLend.properties.startDate}
					</TypographyParagraph>
				</div>
				<div
					id="endDate"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(offchainLend.properties.endDate)
						tooltipCopyEndDate.current?.open({
							anchorSelect: '#endDate',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyEndDate.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyEndDate} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						End date:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainLend.properties.endDate}
					</TypographyParagraph>
				</div>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="lenderAddress"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(offchainLend.properties.ownerAddress)
						tooltipCopyLenderAddress.current?.open({
							anchorSelect: '#lenderAddress',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyLenderAddress.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyLenderAddress} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Lender Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
							>
								...
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									offchainLend.properties.ownerAddress,
								))()}
						>
							{(ownerAddress: string) => {
								return (
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
									>
										{ownerAddress}
									</TypographyParagraph>
								)
							}}
						</Await>
					</Suspense>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all"
					>
						{truncateStringInMiddle(offchainLend.properties.ownerAddress)}
					</TypographyParagraph>
				</div>
				<div
					id="lendeeAddress"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(
							offchainLend.properties.receiverAddress,
						)
						tooltipCopyLendeeAddress.current?.open({
							anchorSelect: '#lendeeAddress',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyLendeeAddress.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyLendeeAddress} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Lendee Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
							>
								...{' '}
								{mapStatus(offchainLend) == 'Pending' ? (
									<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
										Pending
									</div>
								) : (
									''
								)}
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									offchainLend.properties.receiverAddress,
								))()}
						>
							{(receiverAddress: string) => {
								return (
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
									>
										{receiverAddress}{' '}
										{mapStatus(offchainLend) == 'Pending' ? (
											<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
												Pending
											</div>
										) : (
											''
										)}
									</TypographyParagraph>
								)
							}}
						</Await>
					</Suspense>
				</div>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="amount"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(offchainLend.properties.amount)
						tooltipCopyAmount.current?.open({
							anchorSelect: '#amount',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyAmount.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyAmount} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Amount:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainLend.properties.amount}
					</TypographyParagraph>
				</div>
				<div
					id="currency"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(offchainLend.properties.currency)
						tooltipCopyCurrency.current?.open({
							anchorSelect: '#currency',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyCurrency.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyCurrency} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Currency:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{offchainLend.properties.currency}
					</TypographyParagraph>
				</div>
			</div>
			<div
				id="comment"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(offchainLend.properties.description)
					tooltipCopyComment.current?.open({
						anchorSelect: '#currency',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyComment.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyComment} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Comment:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{offchainLend.properties.description}
				</TypographyParagraph>
			</div>
			<div
				id="signature"
				className="flex flex-row p-4 hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(offchainLend.signature)
					tooltipCopySignature.current?.open({
						anchorSelect: '#signature',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopySignature.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopySignature} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Signature:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{offchainLend.signature}
				</TypographyParagraph>
			</div>
		</>
	)
}
